import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from 'src/root/store';

import type {
  AuthenticateRequest,
  AuthenticateResponse,
  MasqueradeRequest,
  MasqueradeResponse,
  RefreshAuthenticationResponse,
  SocialAuthenticationResponse,
} from '@appTypes/auth';
import { getAuthUrl } from '@config';

const pointlessSelfsessionGuid = 'A1A1A1A1-B2B2-C3C3-D4D4-E5E5E5E5E5E5';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({ baseUrl: getAuthUrl() }),
  endpoints: builder => ({
    logout: builder.mutation({
      query: () => ({
        credentials: 'include',
        url: '/api/users/logout',
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }),
    }),

    authenticate: builder.mutation<
      AuthenticateResponse,
      {
        username: string;
        body: AuthenticateRequest;
      }
    >({
      query: arg => ({
        url: `/api/users/${arg.username}/authenticate`,
        credentials: 'include',
        body: arg.body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.errors,
        headers: {
          Accept: 'application/json; version = 2',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }),
    }),

    sessionRefresh: builder.query<
      RefreshAuthenticationResponse,
      { token?: string }
    >({
      query: arg => {
        const headers: Record<string, string> = {
          Accept: 'application/json; version = 1',
          'Content-Type': 'application/json',
        };

        if (arg.token) {
          headers['Authorization'] = `bearer ${arg.token}`;
        }

        return {
          url: `/api/users/auth/refresh`,
          credentials: 'include',
          headers,
          method: 'GET',
        };
      },
    }),

    // TODO: Replace once social auth supports Apple login
    oldSessionRefresh: builder.query<
      RefreshAuthenticationResponse,
      { token?: string }
    >({
      query: arg => {
        const headers: Record<string, string> = {
          Accept: 'application/json; version = 1',
          'Content-Type': 'application/json',
        };

        if (arg.token) {
          headers['Authorization'] = `bearer ${arg.token}`;
        }

        return {
          url: `/api/users/self_${pointlessSelfsessionGuid}/sessions/current`,
          credentials: 'include',
          headers,
          method: 'GET',
        };
      },
    }),

    masquerade: builder.mutation<MasqueradeResponse, MasqueradeRequest>({
      queryFn: (arg, queryApi, _extraOptions, baseQuery) => {
        const jwt: string = (queryApi.getState() as RootState).user?.loginData
          ?.token;
        return baseQuery({
          url: `/api/users/${arg.memberId}/sessions/masquerade`,
          credentials: 'include',
          validateStatus: (response, result) =>
            response.status === 200 && !result.errors,
          headers: {
            Authorization: `bearer ${jwt}`,
            Accept: 'application/json; version = 1',
            'Content-Type': 'application/json',
          },
          method: 'GET',
        }) as { data: MasqueradeResponse };
      },
    }),

    socialAuthenticate: builder.query<
      SocialAuthenticationResponse,
      { token?: string }
    >({
      query: arg => {
        const headers: Record<string, string> = {
          Accept: 'application/json; version = 1',
          'Content-Type': 'application/json',
        };

        // OAuth2 JWT
        if (arg.token) {
          headers['Authorization'] = `bearer ${arg.token}`;
        }

        return {
          url: `/api/users/auth/social`,
          credentials: 'include',
          headers,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useLogoutMutation,
  useAuthenticateMutation,
  useSessionRefreshQuery,
  useLazySessionRefreshQuery,
  useLazySocialAuthenticateQuery,
} = authApi;
